// src/pages/auth/MFASetupPage.js
// Handles TWO scenarios:
// 1. MFA Challenge — user has MFA enrolled, needs to verify to complete login
// 2. MFA Enrollment — first time setup for a new user

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';

export function MFASetupPage() {
  const { user, mfaResolver } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(() => {
    // If we have a resolver, user is completing a login challenge
    // If we have a user, they are enrolling for the first time
    return 'start';
  });
  const [code, setCode] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('');
  const recaptchaRef = useRef(null);

  const isChallengeMode = !!mfaResolver;

  useEffect(() => {
    if (isChallengeMode) {
      // Show the phone hint from the resolver
      const hint = mfaResolver.hints[0];
      setMaskedPhone(hint?.phoneNumber || 'your phone');
      setStep('challenge');
      // Auto-send the code
      sendChallengeCode();
    } else {
      setStep('intro');
    }
  }, []);

  function initRecaptcha(elementId) {
    try {
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
      recaptchaRef.current = new RecaptchaVerifier(auth, elementId, {
        size: 'invisible',
        callback: () => {},
      });
      return recaptchaRef.current;
    } catch (e) {
      console.error('reCAPTCHA init error:', e);
      return null;
    }
  }

  // ── Challenge flow: complete sign-in for existing MFA user ──────────────────

  async function sendChallengeCode() {
    setLoading(true);
    setError(null);
    try {
      const verifier = initRecaptcha('recaptcha-challenge');
      const phoneInfoOptions = {
        multiFactorHint: mfaResolver.hints[0],
        session: mfaResolver.session,
      };
      const provider = new PhoneAuthProvider(auth);
      const vid = await provider.verifyPhoneNumber(phoneInfoOptions, verifier);
      setVerificationId(vid);
      setStep('challenge-verify');
    } catch (err) {
      console.error(err);
      setError('Failed to send verification code. Please try again.');
      setStep('challenge-retry');
    }
    setLoading(false);
  }

  async function completeChallengeSignIn() {
    setLoading(true);
    setError(null);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      await mfaResolver.resolveSignIn(assertion);
      // Auth state listener in AuthContext will pick up the completed sign-in
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error(err);
      setError('Invalid code. Please try again.');
    }
    setLoading(false);
  }

  // ── Enrollment flow: first-time MFA setup ───────────────────────────────────

  async function sendEnrollmentCode() {
    setLoading(true);
    setError(null);
    try {
      const verifier = initRecaptcha('recaptcha-enroll');
      const session = await multiFactor(user).getSession();
      const phoneInfoOptions = { phoneNumber: phone, session };
      const provider = new PhoneAuthProvider(auth);
      const vid = await provider.verifyPhoneNumber(phoneInfoOptions, verifier);
      setVerificationId(vid);
      setStep('enroll-verify');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to send code. Please try again.');
    }
    setLoading(false);
  }

  async function completeEnrollment() {
    setLoading(true);
    setError(null);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      await multiFactor(user).enroll(assertion, 'Phone');
      await updateDoc(doc(db, 'users', user.uid), { mfaEnrolled: true });
      setStep('done');
      setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
    } catch (err) {
      console.error(err);
      setError('Invalid code. Please try again.');
    }
    setLoading(false);
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <span style={styles.logoMark}>⬡</span>
          <h2 style={styles.title}>
            {isChallengeMode ? 'Verify Your Identity' : 'Set Up Two-Factor Authentication'}
          </h2>
          <p style={styles.subtitle}>
            {isChallengeMode
              ? 'Enter the verification code to complete sign-in.'
              : 'ROOTS requires 2FA on all accounts. This is a one-time setup.'}
          </p>
        </div>

        {/* Hidden reCAPTCHA containers */}
        <div id="recaptcha-challenge" />
        <div id="recaptcha-enroll" />

        {/* Challenge: sending code */}
        {step === 'challenge' && (
          <div style={styles.body}>
            <p style={styles.description}>Sending verification code to {maskedPhone}...</p>
          </div>
        )}

        {/* Challenge: enter code */}
        {step === 'challenge-verify' && (
          <div style={styles.body}>
            <p style={styles.description}>
              Enter the 6-digit code sent to {maskedPhone}
            </p>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              maxLength={6}
              style={styles.codeInput}
              autoFocus
            />
            {error && <div style={styles.error}>{error}</div>}
            <button
              onClick={completeChallengeSignIn}
              disabled={loading || code.length < 6}
              style={{ ...styles.primaryBtn, opacity: loading || code.length < 6 ? 0.6 : 1 }}
            >
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </button>
            <button onClick={() => { setCode(''); sendChallengeCode(); }} style={styles.secondaryBtn}>
              Resend code
            </button>
          </div>
        )}

        {/* Challenge: retry */}
        {step === 'challenge-retry' && (
          <div style={styles.body}>
            {error && <div style={styles.error}>{error}</div>}
            <button onClick={sendChallengeCode} style={styles.primaryBtn}>
              Try Again
            </button>
          </div>
        )}

        {/* Enrollment: intro */}
        {step === 'intro' && (
          <div style={styles.body}>
            <p style={styles.description}>
              You'll receive a verification code via SMS each time you sign in.
            </p>
            <button onClick={() => setStep('enroll-phone')} style={styles.primaryBtn}>
              Set Up 2FA
            </button>
          </div>
        )}

        {/* Enrollment: enter phone */}
        {step === 'enroll-phone' && (
          <div style={styles.body}>
            <label style={styles.label}>Mobile Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 000 0000"
              style={styles.input}
              autoFocus
            />
            {error && <div style={styles.error}>{error}</div>}
            <button
              onClick={sendEnrollmentCode}
              disabled={loading || !phone}
              style={{ ...styles.primaryBtn, opacity: loading || !phone ? 0.6 : 1 }}
            >
              {loading ? 'Sending...' : 'Send Code'}
            </button>
          </div>
        )}

        {/* Enrollment: verify code */}
        {step === 'enroll-verify' && (
          <div style={styles.body}>
            <p style={styles.description}>Enter the 6-digit code sent to {phone}</p>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              maxLength={6}
              style={styles.codeInput}
              autoFocus
            />
            {error && <div style={styles.error}>{error}</div>}
            <button
              onClick={completeEnrollment}
              disabled={loading || code.length < 6}
              style={{ ...styles.primaryBtn, opacity: loading || code.length < 6 ? 0.6 : 1 }}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button onClick={() => setStep('enroll-phone')} style={styles.secondaryBtn}>
              ← Try a different number
            </button>
          </div>
        )}

        {/* Done */}
        {step === 'done' && (
          <div style={{ ...styles.body, textAlign: 'center' }}>
            <div style={styles.successIcon}>✓</div>
            <p style={styles.description}>2FA enrolled. Redirecting to dashboard...</p>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f7f4', fontFamily: "'DM Sans', sans-serif" },
  card: { background: '#fff', borderRadius: 16, padding: '48px 40px', width: '100%', maxWidth: 420, boxShadow: '0 2px 24px rgba(0,0,0,0.07)', border: '1px solid #e8e4de' },
  header: { textAlign: 'center', marginBottom: 32 },
  logoMark: { fontSize: 32, color: '#7eb86a', display: 'block', marginBottom: 12 },
  title: { fontSize: 20, fontWeight: 700, color: '#1a1f16', margin: '0 0 8px' },
  subtitle: { fontSize: 14, color: '#8a9485', margin: 0 },
  body: { display: 'flex', flexDirection: 'column', gap: 14 },
  description: { fontSize: 14, color: '#6b7566', margin: 0 },
  label: { fontSize: 13, fontWeight: 600, color: '#3d4438' },
  input: { padding: '11px 14px', border: '1.5px solid #e0dbd4', borderRadius: 8, fontSize: 15, fontFamily: 'inherit', outline: 'none' },
  codeInput: { padding: '11px 14px', border: '1.5px solid #e0dbd4', borderRadius: 8, fontSize: 24, fontFamily: 'inherit', outline: 'none', textAlign: 'center', letterSpacing: '0.3em' },
  primaryBtn: { padding: '12px', background: '#1a1f16', color: '#f0ede8', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  secondaryBtn: { padding: '10px', background: 'none', color: '#8a9485', border: 'none', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  error: { background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, border: '1px solid #fecaca' },
  successIcon: { fontSize: 48, color: '#7eb86a', marginBottom: 12 },
};
